import React, { useEffect, useRef } from "react";

const FRAUDIO_SRC = "/assets/js/fraudio.min.js";

// One-time global guard: fraudio sets currentTime before duration loads → NaN error
if (typeof HTMLMediaElement !== "undefined" && !HTMLMediaElement.prototype.__currentTimeGuarded) {
    const _desc = Object.getOwnPropertyDescriptor(HTMLMediaElement.prototype, "currentTime");
    Object.defineProperty(HTMLMediaElement.prototype, "currentTime", {
        set(v) { if (isFinite(v) && !isNaN(v)) _desc.set.call(this, v); },
        get() { return _desc.get.call(this); },
        configurable: true,
    });
    HTMLMediaElement.prototype.__currentTimeGuarded = true;
}
const PLAY_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="30"><path fill="white" stroke="white" d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm115.7 272l-176 101c-15.8 8.8-35.7-2.5-35.7-21V152c0-18.4 19.8-29.8 35.7-21l176 107c16.4 9.2 16.4 32.9 0 42z"/></svg>`;
const PAUSE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="30"><path fill="white" stroke="white" d="M256 8C119 8 8 119 8 256s111 248 248 248 248-111 248-248S393 8 256 8zm-16 328c0 8.8-7.2 16-16 16h-48c-8.8 0-16-7.2-16-16V176c0-8.8 7.2-16 16-16h48c8.8 0 16 7.2 16 16v160zm112 0c0 8.8-7.2 16-16 16h-48c-8.8 0-16-7.2-16-16V176c0-8.8 7.2-16 16-16h48c8.8 0 16 7.2 16 16v160z"/></svg>`;
const SPINNER_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="30" height="30"><style>@keyframes fs{to{transform:rotate(360deg)}}.fsp{transform-origin:25px 25px;animation:fs 0.7s linear infinite}</style><circle class="fsp" cx="25" cy="25" r="20" fill="none" stroke="white" stroke-width="5" stroke-linecap="round" stroke-dasharray="90 60"/></svg>`;

function FraudioLoader({ src, title = "Yükleniyor", artist = "Ses hazırlanıyor..." }) {
    const containerRef = useRef(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const audio = document.createElement("audio");
        audio.className = "fraudio";
        audio.dataset.title = title;
        audio.dataset.artist = artist;
        audio.preload = "auto";

        const source = document.createElement("source");
        source.src = src;
        source.type = "audio/mpeg";
        audio.appendChild(source);
        container.appendChild(audio);

        const attachListeners = () => {
            const fraudioContainer = container.querySelector(".fraudio-container");
            const fraudioAudio = fraudioContainer?.querySelector("audio");
            const playBtn = fraudioContainer?.querySelector(".fraudio-play");
            const playContainer = fraudioContainer?.querySelector(".fraudio-play-container");
            const progressClick = fraudioContainer?.querySelector(".fraudio-progress-click");
            if (!fraudioAudio || !playBtn || !playContainer) return;

            // Patch currentTime setter so fraudio can never throw on non-finite seeks
            const ctDesc = Object.getOwnPropertyDescriptor(HTMLMediaElement.prototype, 'currentTime');
            Object.defineProperty(fraudioAudio, 'currentTime', {
                set(v) { if (isFinite(v) && !isNaN(v)) ctDesc.set.call(this, v); },
                get() { return ctDesc.get.call(this); },
                configurable: true,
            });

            // show spinner on click before fraudio's handler fires
            playContainer.addEventListener("click", () => {
                if (fraudioAudio.paused) playBtn.innerHTML = SPINNER_ICON;
                else playBtn.innerHTML = PLAY_ICON;
            }, true);

            fraudioAudio.addEventListener("playing", () => {
                playBtn.innerHTML = PAUSE_ICON;
            });

            // only show spinner during playback stall, not during preload
            fraudioAudio.addEventListener("waiting", () => {
                if (!fraudioAudio.paused) playBtn.innerHTML = SPINNER_ICON;
            });

            // guard against NaN duration (metadata not loaded yet)
            if (progressClick) {
                const blockIfNotReady = (e) => {
                    if (!isFinite(fraudioAudio.duration) || isNaN(fraudioAudio.duration)) {
                        e.stopImmediatePropagation();
                        e.stopPropagation();
                        e.preventDefault();
                    }
                };
                progressClick.addEventListener("mousedown", blockIfNotReady, true);
                progressClick.addEventListener("mouseup",   blockIfNotReady, true);
                progressClick.addEventListener("click",     blockIfNotReady, true);
            }
        };

        const doInit = () => {
            if (!window.jQuery) return;
            // skip fraudio() if jQuery ready already initialized this element
            if (!container.querySelector(".fraudio-container")) {
                window.jQuery(audio).fraudio();
            }
            attachListeners();
        };

        if (document.querySelector(`script[src="${FRAUDIO_SRC}"]`)) {
            doInit();
        } else {
            const script = document.createElement("script");
            script.src = FRAUDIO_SRC;
            // setTimeout 0: let fraudio's own jQuery ready handler run first
            script.onload = () => setTimeout(doInit, 0);
            document.body.appendChild(script);
        }

        return () => {
            const wrapper = container.querySelector(".fraudio-container");
            if (wrapper) wrapper.remove();
            else if (container.contains(audio)) audio.remove();
        };
    }, [src, title, artist]);

    return <div ref={containerRef} />;
}

export default FraudioLoader;
